// src/pages7-recipie-ai/Contact.js
import React, { useState, useEffect } from "react";
import { addData, getData, deleteData } from "../service/APIService";
import uuid from "react-uuid";
import { Modal, Button } from "react-bootstrap";
import secureLocalStorage from "react-secure-storage";

const SUPPORT_EMAIL = "support@theuniquecreations.com";

const Contact = ({ isModal = false }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("General Issue");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [supportCases, setSupportCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [dailySubmissionCount, setDailySubmissionCount] = useState(0);
  const [showCases, setShowCases] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const maxcount = 5;

  const categories = [
    { id: "General Issue", label: "General Issue", icon: "🛠️" },
    { id: "Feedback", label: "Feedback & Ideas", icon: "💡" },
    { id: "App Bug", label: "Bug Report", icon: "🐛" },
    { id: "Subscription", label: "Pro & Billing", icon: "💳" },
    { id: "Others", label: "Other", icon: "❓" },
  ];

  const handleCopyEmail = () => {
    navigator.clipboard?.writeText(SUPPORT_EMAIL);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const toggleSupportCases = () => {
    setShowCases((prevState) => !prevState);
  };

  const handleShowDetails = (caseItem) => {
    setSelectedCase(caseItem);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCase(null);
    setShowModal(false);
  };

  useEffect(() => {
    const sessionUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
    if (sessionUser) {
      setName(sessionUser.name || "User");
      setEmail(sessionUser.email || "");
      setIsLoggedIn(true);
      fetchSupportCases(sessionUser.email);
    } else {
      setName("");
      setEmail("");
      setIsLoggedIn(false);
    }
  }, []);

  const fetchSupportCases = async (userEmail) => {
    try {
      const cases = await getData(userEmail, "contactus");
      setSupportCases(cases || []);
      const today = new Date().toISOString().split("T")[0];
      const todayCount = (cases || []).filter((caseItem) => {
        const caseDate = new Date(caseItem.createddate).toISOString().split("T")[0];
        return caseDate === today;
      }).length;
      setDailySubmissionCount(todayCount);
      if (!isLoggedIn && todayCount >= maxcount) {
        setMessage(`Maximum daily limit of ${maxcount} support requests reached.`);
        setMessageType("warning");
      }
    } catch (error) {
      console.error("Failed to fetch support cases:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      setMessage("Please enter a brief description of your request.");
      setMessageType("danger");
      return;
    }

    if (!isLoggedIn && !email.trim()) {
      setMessage("Please enter your email so we can respond to you.");
      setMessageType("danger");
      return;
    }

    const currentDate = new Date().toISOString();
    const contactData = {
      id: uuid(),
      email: email.trim(),
      name: name.trim() || "User",
      subject: category,
      description: description.trim(),
      type: "contactus",
      status: "pending",
      createddate: currentDate,
    };

    setLoading(true);
    setMessage("");

    try {
      if (!isLoggedIn) {
        const cases = await getData(email, "contactus");
        setSupportCases(cases || []);
        const today = new Date().toISOString().split("T")[0];
        const todayCount = (cases || []).filter((caseItem) => {
          const caseDate = new Date(caseItem.createddate).toISOString().split("T")[0];
          return caseDate === today;
        }).length;
        setDailySubmissionCount(todayCount);

        if (todayCount >= maxcount) {
          setMessage(`Daily request limit of ${maxcount} reached for this email.`);
          setMessageType("warning");
          setLoading(false);
          return;
        } else {
          await addData(contactData);
          setMessage("Your support message has been sent successfully!");
          setMessageType("success");
          setDescription("");
          setCategory("General Issue");
        }
      } else {
        await addData(contactData);
        setMessage("Your support message has been sent successfully!");
        setMessageType("success");
        setDescription("");
        setCategory("General Issue");
        fetchSupportCases(email);
      }
    } catch (error) {
      console.error("Failed to save contact details:", error);
      setMessage("Failed to send message. Please try again or email us directly.");
      setMessageType("danger");
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this support ticket?");
    if (!isConfirmed) return;

    try {
      await deleteData(id);
      setSupportCases((prevCases) => prevCases.filter((caseItem) => caseItem.id !== id));
      setMessage("Support ticket removed.");
      setMessageType("success");
    } catch (error) {
      console.error("Failed to delete case:", error);
      setMessage("Failed to delete the ticket. Please try again.");
      setMessageType("danger");
    }
  };

  return (
    <div className="support-container py-1">
      {/* ── 1. Hero Support Header ── */}
      {!isModal ? (
        <div className="support-hub-card mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-success"
                style={{
                  width: "42px",
                  height: "42px",
                  background: "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.08) 100%)",
                  border: "1px solid rgba(34, 197, 94, 0.3)",
                  fontSize: "1.2rem",
                }}
              >
                🎧
              </div>
              <div>
                <h5 className="fw-bolder text-dark mb-0" style={{ letterSpacing: "-0.3px" }}>
                  Support & Contact
                </h5>
                <small className="text-secondary" style={{ fontSize: "11.5px" }}>
                  {isLoggedIn ? "Registered User Support" : "Guest Support"}
                </small>
              </div>
            </div>
            <span className="badge rounded-pill bg-success bg-opacity-15 text-success border border-success border-opacity-25 px-2.5 py-1" style={{ fontSize: "10.5px" }}>
              ⚡ Replies &lt; 24h
            </span>
          </div>

          <p className="text-secondary small mb-3" style={{ fontSize: "12.5px", lineHeight: "1.4" }}>
            Have a question about recipes, food scanning, or your account? Submit a message below or email us directly.
          </p>

          {/* Direct Email Action Pill */}
          <div
            className="d-flex align-items-center justify-content-between p-2 px-3 rounded-pill"
            style={{
              background: "rgba(255, 255, 255, 0.85)",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "inset 0 1px 1px #ffffff",
            }}
          >
            <div className="d-flex align-items-center gap-2 min-w-0">
              <i className="fi fi-rr-envelope text-success" style={{ fontSize: "0.95rem" }}></i>
              <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "12px" }}>
                {SUPPORT_EMAIL}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border rounded-pill py-0.5 px-2.5 text-secondary fw-semibold d-inline-flex align-items-center gap-1"
              style={{ fontSize: "11px" }}
              onClick={handleCopyEmail}
            >
              {copiedEmail ? (
                <>
                  <i className="fi fi-rr-check text-success"></i> Copied
                </>
              ) : (
                <>
                  <i className="fi fi-rr-copy"></i> Copy
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between p-2 px-3 rounded-pill"
            style={{
              background: "rgba(34, 197, 94, 0.08)",
              border: "1px solid rgba(34, 197, 94, 0.22)",
              boxShadow: "inset 0 1px 1px #ffffff",
            }}
          >
            <div className="d-flex align-items-center gap-2 min-w-0">
              <i className="fi fi-rr-envelope text-success" style={{ fontSize: "0.95rem" }}></i>
              <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "12px" }}>
                {SUPPORT_EMAIL}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border rounded-pill py-0.5 px-2.5 text-secondary fw-semibold d-inline-flex align-items-center gap-1"
              style={{ fontSize: "11px" }}
              onClick={handleCopyEmail}
            >
              {copiedEmail ? (
                <>
                  <i className="fi fi-rr-check text-success"></i> Copied
                </>
              ) : (
                <>
                  <i className="fi fi-rr-copy"></i> Copy
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── 2. Message Submission Form ── */}
      <div className="app-card p-3 mb-3">
        <form onSubmit={handleSubmit}>
          {/* Category Selector Pills */}
          <div className="mb-3">
            <label className="fw-bold text-dark small mb-2 d-block">Select Topic</label>
            <div className="d-flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`support-category-chip ${category === cat.id ? "active" : ""}`}
                  onClick={() => setCategory(cat.id)}
                >
                  <span className="me-1">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Name & Email Fields */}
          <div className="row g-2 mb-2.5">
            <div className="col-12 col-md-6">
              <label className="fw-semibold text-secondary small mb-1">Your Name</label>
              <div className="input-group-seamless">
                <span className="input-group-text">
                  <i className="fi fi-rr-user"></i>
                </span>
                <input
                  type="text"
                  value={name}
                  maxLength={40}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  disabled={isLoggedIn}
                />
              </div>
            </div>

            <div className="col-12 col-md-6">
              <label className="fw-semibold text-secondary small mb-1">Your Email</label>
              <div className="input-group-seamless">
                <span className="input-group-text">
                  <i className="fi fi-rr-envelope"></i>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={isLoggedIn}
                  required
                />
              </div>
            </div>
          </div>

          {/* Description Textarea */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="fw-semibold text-secondary small">How can we help?</label>
              <span className="text-muted" style={{ fontSize: "11px" }}>
                {500 - description.length} chars left
              </span>
            </div>
            <textarea
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control rounded-4 p-2.5"
              style={{
                fontSize: "13.5px",
                background: "rgba(255, 255, 255, 0.9)",
                border: "1.5px solid #e2e8f0",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
                resize: "none",
                minHeight: "100px",
              }}
              placeholder="Please describe your issue, recipe feedback, or question in detail..."
              required
            />
          </div>

          {/* Status Message Alert */}
          {message && (
            <div
              className={`alert alert-${messageType} py-2 px-3 rounded-3 small d-flex align-items-center gap-2 mb-3`}
              style={{ fontSize: "12.5px" }}
            >
              <i className={`fi ${messageType === "success" ? "fi-rr-check-circle" : "fi-rr-info"}`}></i>
              <span>{message}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-success w-100 py-2.5 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
            disabled={loading || dailySubmissionCount >= maxcount}
            style={{
              background: "linear-gradient(180deg, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 50%, rgba(0, 0, 0, 0.08) 100%), #22c55e",
              border: "1px solid rgba(255, 255, 255, 0.35)",
              boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 6px 18px rgba(34, 197, 94, 0.35)",
            }}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Sending Ticket...</span>
              </>
            ) : dailySubmissionCount >= maxcount ? (
              <span>Daily Limit Reached ({maxcount}/day)</span>
            ) : (
              <>
                <i className="fi fi-rr-paper-plane"></i>
                <span>Send Support Message</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* ── 3. Support Tickets History Section ── */}
      {isLoggedIn && (
        <div className="mt-3">
          <div className="d-flex align-items-center justify-content-between mb-2 px-1">
            <span className="fw-bold text-dark small">My Support History</span>
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none text-success fw-bold p-0 small"
              onClick={toggleSupportCases}
              style={{ fontSize: "12px" }}
            >
              {showCases ? "Hide Tickets ▲" : `View Tickets (${supportCases.length}) ▼`}
            </button>
          </div>

          {showCases && (
            <div className="mt-2">
              {supportCases.length === 0 ? (
                <div className="app-card p-3 text-center text-secondary small">
                  No support tickets submitted yet.
                </div>
              ) : (
                supportCases.slice(0, 10).map((caseItem) => (
                  <div key={caseItem.id} className="support-ticket-card">
                    <div className="d-flex align-items-center justify-content-between mb-1.5">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge rounded-pill bg-light border text-dark fw-bold" style={{ fontSize: "11px" }}>
                          {caseItem.subject || "General Issue"}
                        </span>
                        <span
                          className={`badge rounded-pill ${
                            caseItem.status === "resolved"
                              ? "bg-success bg-opacity-20 text-success border border-success border-opacity-25"
                              : "bg-warning bg-opacity-20 text-warning border border-warning border-opacity-25"
                          }`}
                          style={{ fontSize: "10px" }}
                        >
                          ● {caseItem.status || "pending"}
                        </span>
                      </div>
                      <small className="text-muted" style={{ fontSize: "11px" }}>
                        {new Date(caseItem.createddate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </small>
                    </div>

                    <p
                      className="text-secondary small mb-2 text-truncate"
                      style={{ fontSize: "12px", cursor: "pointer" }}
                      onClick={() => handleShowDetails(caseItem)}
                      title="Click to view details"
                    >
                      {caseItem.description}
                    </p>

                    <div className="d-flex align-items-center justify-content-between pt-1 border-top">
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-success p-0 text-decoration-none fw-semibold"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => handleShowDetails(caseItem)}
                      >
                        View Details &rarr;
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => deleteCase(caseItem.id)}
                        title="Delete ticket"
                      >
                        <i className="fi fi-rr-trash me-1"></i> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Case Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <div className="modal-header border-bottom">
          <h5 className="modal-title fw-bold text-dark" style={{ fontSize: "1rem" }}>
            Ticket Details
          </h5>
          <button type="button" className="btn-close" onClick={handleCloseModal} />
        </div>
        <Modal.Body className="p-3">
          {selectedCase ? (
            <div>
              <div className="mb-2.5 pb-2 border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-secondary d-block" style={{ fontSize: "11px" }}>Topic</small>
                  <span className="fw-bold text-dark">{selectedCase.subject}</span>
                </div>
                <span
                  className={`badge rounded-pill ${
                    selectedCase.status === "resolved"
                      ? "bg-success bg-opacity-20 text-success"
                      : "bg-warning bg-opacity-20 text-warning"
                  }`}
                  style={{ fontSize: "11px" }}
                >
                  {selectedCase.status || "pending"}
                </span>
              </div>

              <div className="mb-2.5 pb-2 border-bottom">
                <small className="text-secondary d-block" style={{ fontSize: "11px" }}>Message</small>
                <p className="text-dark mb-0 small mt-1" style={{ whiteSpace: "pre-wrap", lineHeight: "1.4" }}>
                  {selectedCase.description}
                </p>
              </div>

              <div className="row g-2 text-secondary" style={{ fontSize: "11.5px" }}>
                <div className="col-6">
                  <span className="d-block text-muted">Created</span>
                  <span>{new Date(selectedCase.createddate).toLocaleString()}</span>
                </div>
                <div className="col-6 text-end">
                  <span className="d-block text-muted">Ticket ID</span>
                  <span className="font-monospace text-truncate d-inline-block" style={{ maxWidth: "120px" }}>
                    {selectedCase.id}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted small">Loading ticket details...</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" className="rounded-pill px-4 border" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Contact;
